import sliderStyle from '../slider.module.scss';

import icn_table_cell_Unverified from 'assets/images/icons/icn_table_cell_Unverified.svg';
import icn_table_cell_Verified from 'assets/images/icons/icn_table_cell_Verified.svg';

import { useState, useEffect } from 'react';
import { Box } from "@mui/material";
import { v4 as uuidv4 } from "uuid";

import useDataSourceManager from "hooks/useDataSourceManager";
import { isValidIpV4, isSameSubnet } from "utils/network-validation";
import { defaultIpModeState, authStateOption, otherTimeout } from "../../../const";

import { SolidButton } from "components/buttons/SolidButton";
import { RadioButton } from 'components/buttons/RadioButton';
import ModalContainer from 'components/ModalContainer';
import IpInputField from "components/InputField/IpInputField";
import ModalIpSettingHint from '../../../modals/modalIpSettingHint';
import { getAPIKeyAndParams } from '../../../requests';

const checkMockup = process.env.REACT_APP_IS_MOCKUP && process.env.REACT_APP_IS_MOCKUP === 'true' ? true : false;

const IpSettings = (props) => {
  const { auth, device, onToggleSlider, isNotSupportDNSDevice } = props;
  const { sendRequest, dataMap, getData } = useDataSourceManager();

  // Status
  const [showModalIpSettingHint, setShowModalIpSettingHint] = useState(false);
  const [ipModeOptions, setIpModeOptions] = useState([]);
  const [ipAddress, setIpAddress] = useState('');
  const [subnetMask, setSubnetMask] = useState('');
  const [gateway, setGateway] = useState('');
  const [dns1, setDns1] = useState('');
  const [dns2, setDns2] = useState('');
  const [isError, setIsError] = useState({
    ipAddress: false,
    subnetMask: false,
    gateway: false,
    dns1: false,
    dns2: false,
  });
  const [isSending, setIsSending] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [applyError, setApplyError] = useState(false);
  const [socketId, setSocketId] = useState(null);
  const [timeoutId, setTimeoutId] = useState(null);

  // Method
  const changeIpMode = (value) => {
    setIsSubmitted(false);
    setApplyError(false);

    const updatedIpModeOptions = ipModeOptions.map((option) => ({
      ...option,
      selected: option.value === value,
    }));

    setIpModeOptions(updatedIpModeOptions);

    if (value === defaultIpModeState.static.value) {
      setIpAddress('');
      setSubnetMask('');
      setGateway('');
      setDns1('');
      setDns2('');
    }
  }

  const checkForm = () => {
    let result = true;

    if (ipModeOptions.find((option) => option.selected)?.value === defaultIpModeState.static.value) {
      if (isNotSupportDNSDevice(device.modelName)) {
        if (ipAddress === '' || ipAddress === '0.0.0.0' || subnetMask === '' || gateway === '') {
          result = false;
        }

        if (isError.ipAddress || isError.subnetMask || isError.gateway) {
          result = false;
        }
      } else {
        if (ipAddress === '' || ipAddress === '0.0.0.0' || subnetMask === '' || gateway === '' || dns1 === '' || dns1 === '0.0.0.0' || dns2 === '' || dns2 === '0.0.0.0') {
          result = false;
        }

        if (isError.ipAddress || isError.subnetMask || isError.gateway || isError.dns1 || isError.dns2) {
          result = false;
        }
      }
    }

    return result;
  }

  const cleanForm = () => {
    setIpAddress('');
    setSubnetMask('');
    setGateway('');
    setDns1('');
    setDns2('');
    setIsError({
      ipAddress: false,
      subnetMask: false,
      gateway: false,
      dns1: false,
      dns2: false,
    });
    setApplyError(false);
    onToggleSlider();
  }

  const openHint = (flag) => {
    if (device.authStatus === authStateOption.failed) {
      return;
    }

    if (!flag) {
      setShowModalIpSettingHint(false);
      return;
    }

    if (checkForm()) {
      setShowModalIpSettingHint(true);
    }
  }

  const submit = async () => {
    if (device.authStatus === authStateOption.failed) {
      return;
    }

    const newSocketId = uuidv4();
    const { apiKey, placeholder } = getAPIKeyAndParams(auth, 'ipSetting');

    const param = {
      timeout: otherTimeout,
      data: [],
    };

    const ipMode = ipModeOptions.find((option) => option.selected);
    if (ipMode.value === defaultIpModeState.static.value) {
      if (device.layer === 2) {
        param.data.push(
          {
            layer: 2,
            ddpVersion: device.ddpVersion,
            destMacAddress: device.macAddress,
            username: device.username,
            password: device.password,
            deviceIpType: 0,
            dhcpEnable: 0,
            deviceIp: ipAddress,
            subnetMask: subnetMask,
            defaultGateway: gateway,
            deviceDNSType: dns1.length === 0 ? null : 0,
            primaryDNS: dns1.length === 0 ? null : dns1,
            secondaryDNS: dns2.length === 0 ? null : dns2,
          }
        );
      } else {
        param.data.push(
          {
            layer: 3,
            ipVersion: 4,
            ddpVersion: device.ddpVersion,
            destMacAddress: device.macAddress,
            destIpAddress: device.ipAddress,
            username: device.username,
            password: device.password,
            deviceIpType: 0,
            dhcpEnable: 0,
            deviceIp: ipAddress,
            subnetMask: subnetMask,
            defaultGateway: gateway,
            deviceDNSType: dns1.length === 0 ? null : 0,
            primaryDNS: dns1.length === 0 ? null : dns1,
            secondaryDNS: dns2.length === 0 ? null : dns2,
          }
        );
      }
    } else {
      if (device.layer === 2) {
        param.data.push({
          layer: 2,
          ddpVersion: device.ddpVersion,
          destMacAddress: device.macAddress,
          username: device.username,
          password: device.password,
          deviceIpType: 0,
          dhcpEnable: 1,
          specifyCustomizedDNS: isNotSupportDNSDevice(device.modelName) ? null : 0,
        });
      } else {
        param.data.push({
          layer: 3,
          ipVersion: 4,
          ddpVersion: device.ddpVersion,
          destMacAddress: device.macAddress,
          destIpAddress: device.ipAddress,
          username: device.username,
          password: device.password,
          deviceIpType: 0,
          dhcpEnable: 1,
          specifyCustomizedDNS: isNotSupportDNSDevice(device.modelName) ? null : 0,
        });
      }
    };

    sendRequest(
      apiKey,
      newSocketId,
      placeholder,
      param,
    );

    setIsSending(true);
    setIsSubmitted(false);
    setApplyError(false);
    setSocketId(newSocketId);
    setApplyError(false);

    // Set 5-minute timeout (300000 ms)
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    const id = setTimeout(() => {
      setIsSending(false);
      setIsSubmitted(true);
      setApplyError(true);
      setSocketId(null);
    }, 5 * 60 * 1000);

    setTimeoutId(id);
  }

  // Side effect
  useEffect(() => {
    const updatedIpModeOptions = Object.values(defaultIpModeState).map((option) => ({
      ...option,
      selected: false,
    }));
    if (Number(device?.dhcpEnable) === 1) {
      const dhcpOption = updatedIpModeOptions.find((opt) => opt.value === defaultIpModeState.dhcp.value);
      if (dhcpOption) {
        dhcpOption.selected = true;
      }
    } else {
      updatedIpModeOptions[0].selected = true;
    }
    setIpModeOptions(updatedIpModeOptions);
  }, [device?.macAddress]);

  useEffect(() => {
    const response = getData(socketId);

    if (!response) {
      return;
    }

    const { resource, data } = response;
    if (!resource) {
      return;
    }

    let result = null;
    if (checkMockup) {
      // Find out the corresponding device from data
      result = data?.find((item) => item.macAddress === device.macAddress);
    } else {
      result = data?.[0];
    }

    // Avoid error when the data is empty.
    if (!result) {
      result = { code: -1, macAddress: device.macAddress, message: "No response received" };
    }

    // Clear timeout after receiving response
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }

    if (result.code === 0) {
      setApplyError(false)
    } else {
      setApplyError(true);
    }

    setIsSubmitted(true);
    setIsSending(false);
    setSocketId(null);
  }, [dataMap]);

  // 監聽子網段並即時同步到 isError.gateway
  useEffect(() => {
    if (ipModeOptions.find((option) => option.selected)?.value !== defaultIpModeState.static.value) {
      return;
    }

    const ipValid = isValidIpV4(ipAddress) && ipAddress !== '0.0.0.0';
    const subnetValid = isValidIpV4(subnetMask);
    const gatewayValid = isValidIpV4(gateway);

    if (ipValid && subnetValid && gatewayValid) {
      const isSubnetMatch = isSameSubnet(ipAddress, gateway, subnetMask);
      setIsError(prev => ({
        ...prev,
        gateway: !isSubnetMatch
      }));
    } else {
      setIsError(prev => ({
        ...prev,
        gateway: gateway !== '' ? !isValidIpV4(gateway) : false
      }));
    }
  }, [ipAddress, subnetMask, gateway, ipModeOptions]);

  return (
    <>
      <Box className={sliderStyle['field-container']} style={{ width: '500px' }}>
        <Box className={sliderStyle['container']}>
          <Box className={sliderStyle['field']}>
            IP Mode
          </Box>
        </Box>
        <Box className={sliderStyle['container']}>
          <Box className={sliderStyle['value']}>
            {
              ipModeOptions.map((item, index) => (
                <RadioButton
                  key={index}
                  label={item.label}
                  value={item.value}
                  checked={item.selected}
                  onChange={() => changeIpMode(item.value)}
                  name="ipMode"
                />
              ))
            }
          </Box>
        </Box>
        {
          ipModeOptions.find((option) => option.selected)?.value === defaultIpModeState.static.value && (
            <>
              <Box className={sliderStyle['container']}>
                <Box className={sliderStyle['field']}>
                  IP Address
                </Box>
              </Box>
              <Box className={sliderStyle['container']}>
                <>
                  <Box
                    className={sliderStyle['value']}
                    sx={isError.ipAddress ? { '& > div': { borderColor: 'red !important' } } : {}}
                  >
                    <IpInputField
                      value={ipAddress}
                      onChange={(value) => {
                        setIpAddress(value);
                        setIsError({ ...isError, ipAddress: !isValidIpV4(value) || value === '0.0.0.0' });
                      }}
                    />
                  </Box>
                  {
                    isError.ipAddress && (
                      <Box className={sliderStyle['error']}>
                        Invalid format
                      </Box>
                    )
                  }
                </>
              </Box>
              <Box className={sliderStyle['container']}>
                <Box className={sliderStyle['field']}>
                  Subnet Mask
                </Box>
              </Box>
              <Box className={sliderStyle['container']}>
                <>
                  <Box
                    className={sliderStyle['value']}
                    sx={isError.subnetMask ? { '& > div': { borderColor: 'red !important' } } : {}}
                  >
                    <IpInputField
                      value={subnetMask}
                      onChange={(value) => {
                        setSubnetMask(value);
                        setIsError({ ...isError, subnetMask: !isValidIpV4(value) });
                      }}
                    />
                  </Box>
                  {
                    isError.subnetMask && (
                      <Box className={sliderStyle['error']}>
                        Invalid format
                      </Box>
                    )
                  }
                </>
              </Box>
              <Box className={sliderStyle['container']}>
                <Box className={sliderStyle['field']}>
                  Default Gateway
                </Box>
              </Box>
              <Box className={sliderStyle['container']}>
                <>
                  <Box
                    className={sliderStyle['value']}
                    sx={isError.gateway ? { '& > div': { borderColor: 'red !important' } } : {}}
                  >
                    <IpInputField
                      value={gateway}
                      onChange={(value) => {
                        setGateway(value);
                        setIsError({ ...isError, gateway: !isValidIpV4(value) });
                      }}
                    />
                  </Box>
                  {
                    isError.gateway && (
                      <Box className={sliderStyle['error']}>
                        Invalid format
                      </Box>
                    )
                  }
                </>
              </Box>
              {
                !isNotSupportDNSDevice(device.modelName) && (
                  <>
                    <Box className={sliderStyle['container']}>
                      <Box className={sliderStyle['field']}>
                        Primary DNS Server
                      </Box>
                    </Box>
                    <Box className={sliderStyle['container']}>
                      <>
                        <Box className={sliderStyle['value']}>
                          <IpInputField
                            value={dns1}
                            onChange={(value) => {
                              setDns1(value);
                              setIsError({ ...isError, dns1: !isValidIpV4(value) || value === '0.0.0.0' });
                            }}
                          />
                        </Box>
                        {
                          isError.dns1 && (
                            <Box className={sliderStyle['error']}>
                              Invalid format
                            </Box>
                          )
                        }
                      </>
                    </Box>
                    <Box className={sliderStyle['container']}>
                      <Box className={sliderStyle['field']}>
                        Secondary DNS Server
                      </Box>
                    </Box>
                    <Box className={sliderStyle['container']}>
                      <>
                        <Box className={sliderStyle['value']}>
                          <IpInputField
                            value={dns2}
                            onChange={(value) => {
                              setDns2(value);
                              setIsError({ ...isError, dns2: !isValidIpV4(value) || value === '0.0.0.0' });
                            }}
                          />
                        </Box>
                        {
                          isError.dns2 && (
                            <Box className={sliderStyle['error']}>
                              Invalid format
                            </Box>
                          )
                        }
                      </>
                    </Box>
                  </>
                )
              }
            </>
          )
        }
      </Box>
      {
        isSubmitted && (
          applyError ? (
            <>
              <div className={sliderStyle['result']}>
                <div className={sliderStyle['failed']}>
                  <img src={icn_table_cell_Unverified} alt="" />
                  <span className={sliderStyle['text']}>Failed</span>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className={sliderStyle['result']}>
                <div className={sliderStyle['success']}>
                  <img src={icn_table_cell_Verified} alt="" />
                  <span className={sliderStyle['text']}>Success</span>
                </div>
              </div>
            </>
          )
        )
      }
      <Box
        className={sliderStyle['button-container']}
        onClick={() => { }}
      >
        <div className={`${sliderStyle['cancel-btn']}`} onClick={cleanForm}>
          <span className={sliderStyle['text']}>Cancel</span>
        </div>
        <SolidButton
          className={`
            ${sliderStyle['apply-btn']} 
            ${checkForm() ? '' : sliderStyle['disabled_btn']} 
            ${isSending ? sliderStyle['disabled_btn'] : ''}
            ${device.authStatus === authStateOption.failed ? sliderStyle['disabled_btn'] : ''}
          `
          }
          disabled={!checkForm() || isSending || device.authStatus === authStateOption.failed}
          onClick={() => {
            if (!checkForm() || isSending || device.authStatus === authStateOption.failed) {
              return;
            }
            openHint(true);
          }}
        >
          <span className={sliderStyle['text']}>Apply</span>
        </SolidButton>
      </Box>
      {/* IP Settings Hint */}
      <ModalContainer
        display={showModalIpSettingHint}
        modalWidth={'550px'}
        children={
          <ModalIpSettingHint
            title="IP Settings"
            content="Please ensure the devices' IP settings allow them to reach the controller before proceeding. Incorrect IP settings may prevent the devices from being adopted successfully."
            fnCallback={() => {
              setShowModalIpSettingHint(false);
              submit();
            }}
            closeOpenModal={openHint}
          />
        }
      />
    </>
  );
}

export default IpSettings;